import React, { useState, useEffect } from 'react';
import './AudioMixer.css';

const AudioMixer = ({ tracks, onVolumeChange, onMuteToggle, onSoloToggle, isPlaying, onPlayPause }) => {
  const [message, setMessage] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [vuLevels, setVuLevels] = useState([0.2, 0.3, 0.4, 0.2]);
  const [visibleTracks, setVisibleTracks] = useState([]);
  
  // Handle ESC key to close instructions
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && showInstructions) {
        setShowInstructions(false);
      }
    };
    
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showInstructions]);

  // Track visibility changes to show messages
  useEffect(() => {
    const visibleTracks = tracks.filter(track => track.visible);
    
    // Show message when a new track becomes visible
    if (visibleTracks.length > 1) {
      const latestTrack = visibleTracks[visibleTracks.length - 1];
      setMessage(`Great job! Now mix in the ${latestTrack.name}!`);
      
      // Clear message after 3 seconds
      const timer = setTimeout(() => {
        setMessage(null);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [tracks]);
  
  // Track visibility changes to animate new tracks
  useEffect(() => {
    const newVisibleTracks = tracks.filter(track => track.visible);
    if (newVisibleTracks.length > visibleTracks.length) {
      setVisibleTracks(newVisibleTracks);
    }
  }, [tracks]);
  
  // Animate VU meters
  useEffect(() => {
    const interval = setInterval(() => {
      // Generate random values for each VU meter
      const newLevels = vuLevels.map((level) => {
        // Calculate the new level with a bit of randomness, but weighted towards current level
        const change = (Math.random() - 0.5) * 0.3;
        
        // Calculate new level, keeping within 0-1 range
        let newLevel = level + change;
        
        // Ensure it's within bounds and has a minimum value
        newLevel = Math.max(0.1, Math.min(0.9, newLevel));
        
        // For visible tracks, increase the meter slightly
        const visibleTrackCount = visibleTracks.length;
        if (visibleTrackCount > 0) {
          // Adjust levels based on number of visible tracks
          newLevel = Math.max(newLevel, visibleTrackCount * 0.15);
        }
        
        return newLevel;
      });
      
      setVuLevels(newLevels);
    }, 300);
    
    return () => clearInterval(interval);
  }, [vuLevels, visibleTracks]);
  
  return (
    <div className="mixer-container">
      <div className="mixer-frame">
        {/* TASCAM Label */}
        {/* <div className="mixer-branding">
          <div className="brand-name">TASCAM</div>
          <div className="model-name">M-30</div>
        </div> */}

        {/* Play/Pause Button */}
        {/* <button 
          className={`play-pause-button ${isPlaying ? 'paused' : ''}`}
          onClick={onPlayPause}
          title="Play/Pause (Spacebar)"
        /> */}

        {/* VU Meters Section */}
        {/* <div className="vu-meter-section">
          {vuLevels.map((level, index) => (
            <div key={index} className="vu-meter">
              <span className="vu-label">{index + 1}</span>
              <div className="vu-needle" style={{ transform: `rotate(${-60 + level * 120}deg)` }}></div>
            </div>
          ))}
        </div> */}

        {/* Channel Strips */}
        <div className="channel-strips">
          {tracks
            .filter(track => track.visible)
            .map((track, index) => (
              <div 
                key={track.id} 
                className="channel-strip"
                style={{
                  '--animation-order': index // Custom property for staggered animation
                }}
              >
                {/* Overload indicator */}
                <div className={`overload-indicator ${track.volume > 0.9 ? 'active' : ''}`}>
                  <span>OVERLOAD</span>
                </div>
                
                {/* EQ Knobs */}
                {/* <div className="eq-section">
                  <div className="knob red">
                    <div className="knob-line"></div>
                  </div>
                  <div className="knob red">
                    <div className="knob-line"></div>
                  </div>
                  <div className="knob green">
                    <div className="knob-line"></div>
                  </div>
                </div> */}

                {/* Aux Sends */}
                {/* <div className="aux-section">
                  <div className="knob blue">
                    <div className="knob-line"></div>
                  </div>
                  <div className="knob orange">
                    <div className="knob-line"></div>
                  </div>
                </div> */}

                {/* LED Meters */}
                <div className="led-meters">
                  <div className={`led red ${track.volume > 0.7 ? 'active' : ''}`}></div>
                  <div className={`led yellow ${track.volume > 0.4 ? 'active' : ''}`}></div>
                  <div className={`led green ${track.volume > 0.2 ? 'active' : ''}`}></div>
                  <div className={`led green ${track.volume > 0.1 ? 'active' : ''}`}></div>
                </div>

                {/* Channel Controls */}
                <div className="channel-controls">
                  <button 
                    className={`control-btn mute ${track.mute ? 'active' : ''}`}
                    onClick={() => onMuteToggle(track.id)}
                  >
                    M
                  </button>
                  <button 
                    className={`control-btn solo ${track.solo ? 'active' : ''}`}
                    onClick={() => onSoloToggle(track.id)}
                  >
                    S
                  </button>
                </div>

                {/* Fader Section */}
                <div className="fader-container">
                  <div className="fader-slot">
                    <div className="fader-tick-marks">
                      {[...Array(11)].map((_, i) => (
                        <div key={i} className="fader-tick" />
                      ))}
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.01"
                      value={track.volume}
                      onChange={(e) => onVolumeChange(track.id, parseFloat(e.target.value))}
                      className="fader"
                    />
                  </div>
                  <div className="channel-label">
                    <span className="channel-number">{index + 1}</span>
                    <span className="track-name">{track.name}</span>
                  </div>
                </div>
              </div>
            ))}
        </div>
        
        {/* Help Button */}
        {!showInstructions && (
          <button 
            className="help-button" 
            onClick={() => setShowInstructions(true)}
            title="Show Instructions"
          >
            ?
          </button>
        )}
      </div>

      {/* Mixer Instructions */}
      {showInstructions && (
        <>
          <div className="popup-backdrop" onClick={() => setShowInstructions(false)}></div>
          <div className="instructions-popup">
            <div className="instructions-panel">
              <button className="close-instructions" onClick={() => setShowInstructions(false)}>×</button>
              <h3>MIXER INSTRUCTIONS</h3>
              <div className="instructions-content">
                <p>Adjust the volume sliders to mix in each layer of the song.</p>
                <p>When a track reaches 70% volume, the next layer will appear!</p>
                <p>Use the M button to mute a track, and S to solo it.</p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Track Added Message */}
      {/* {message && (
        <div className="message-overlay">
          <div className="message">
            {message}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default AudioMixer;
